import React, { useEffect, useState, useMemo } from 'react';

import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';
import { Button, Card, DatePicker, Tooltip, Select, Tabs, Space, Typography, message } from 'antd';
import { StockOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';

import { singaporeMoment, nowInSingapore, validateDateRange, getTimezoneDisplayText } from '../../utils/timezone';
import _ from 'lodash';

import * as api from '../../api';
import { Usage, Account, ConversationData, PricingAnalytics } from '../../types';
import UsageTable from './UsageTable';
import UsageChart from './UsageChart';
import UsagePricingCards from './UsagePricingCards';
import ExportDropdown from './ExportDropdown';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

type AnalyticsChart = (ConversationData & { date : string})[];
type Collector = {
  "Date": string;
  "Phone Number": string;
  "Conversation Type": string;
  "Initiated By": string;
  "Number of Conversation": number;
  "Charges": string;
}

type PricingCollector = {
  "Date Range": string;
  "WABA ID": string;
  "Total Messages Sent": number;
  "Total Messages Delivered": number;
  "Total Messages Received": number;
  "Marketing Messages": number;
  "Utility Messages": number;
  "Authentication Messages": number;
  "Authentication International": number;
  "Service Messages": number;
  "Free Customer Service": number;
  "Free Entry Point": number;
  "Total Cost": string;
  "Marketing Cost": string;
  "Utility Cost": string;
  "Authentication Cost": string;
  "Authentication International Cost": string;
  "Last Updated": string;
}


function UsagePage() {
  
  const [record, setRecord] = useState<Usage | null>(null);
  const [pricingAnalytics, setPricingAnalytics] = useState<PricingAnalytics | null>(null);
  const [analyticsChart, setAnalyticsChart] = useState<AnalyticsChart>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dates, setDates] = useState<RangeValue<Moment>>([singaporeMoment(), singaporeMoment()]);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const [isInputDisabled, setInputDisable] = useState<boolean>(false);
  const [isPricingLoading, setIsPricingLoading] = useState<boolean>(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pricingLastUpdated, setPricingLastUpdated] = useState<Date | null>(null);
  const [filtedWabaId, setFiltedWabaId] = useState<string>();
  const [filtedPhoneNumber, setFiltedPhoneNumber] = useState<string>('all');
  const finalPhoneNumber = useMemo(() => {
    if (filtedPhoneNumber === 'all') return ''

    return filtedPhoneNumber.replace(/\s/g, '')
  }, [filtedPhoneNumber])
  const [isChart, setIsChart] = useState(false);
  const [isDisbledChart, setIsDisbledChart] = useState(false);
  const [activeTab, setActiveTab] = useState<'usage' | 'pricing'>('usage');
  const [analysisView, setAnalysisView] = useState<'table' | 'chart'>('table');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      const { accounts } = await api.account.find(true);
      setAccounts(accounts);
    };
    fetchAccounts();
  }, []);

  // useEffect(() => {
  //   console.log(record)
  // }, [record])

  const fetchUsage = async (queryPath: string) => {
    try {
      setInputDisable(true);
      setUsageError(null);
      setPricingError(null);
      
      const [usageResult, pricingResult] = await Promise.allSettled([
        api.usage.getUsage(queryPath),
        api.usage.getPricingAnalytics(queryPath)
      ]);
      
      // Handle usage data
      if (usageResult.status === 'fulfilled') {
        const usage = usageResult.value;
        setAnalyticsChart(
          usage.dividedDataEachDates.map((x) => {
            return {
              ...x,
              date: moment(new Date(x.start*1000)).format('YYYY/MM/DD'),
            };
          }),
        );
        setRecord(usage);
        setLastUpdated(new Date());
      } else {
        console.error('Usage fetch failed:', usageResult.reason);
        setUsageError(usageResult.reason?.message || 'Failed to fetch usage data');
        setRecord(null);
      }
      
      // Handle pricing data
      if (pricingResult.status === 'fulfilled') {
        setPricingAnalytics(pricingResult.value);
        setPricingLastUpdated(new Date());
      } else {
        console.error('Pricing fetch failed:', pricingResult.reason);
        setPricingError(pricingResult.reason?.message || 'Failed to fetch pricing data');
        setPricingAnalytics(null);
      }
    } catch (error : any) {
      console.error('Unexpected error:', error.message);
      setUsageError('An unexpected error occurred');
      setRecord(null);
      setPricingAnalytics(null);
    } finally {
      setInputDisable(false);
    }
  };

  const fetchPricingOnly = async (queryPath: string) => {
    try {
      setIsPricingLoading(true);
      setPricingError(null);
      
      const pricing = await api.usage.getPricingAnalytics(queryPath);
      setPricingAnalytics(pricing);
      setPricingLastUpdated(new Date());
      
    } catch (error: any) {
      console.error('Pricing fetch failed:', error.message);
      setPricingError(error?.message || 'Failed to fetch pricing data');
      setPricingAnalytics(null);
    } finally {
      setIsPricingLoading(false);
    }
  };

  // Date preset options
  const datePresets = useMemo(() => {
    const today = nowInSingapore();
    return [
      {
        label: 'Today',
        value: 'today',
        range: [today.clone(), today.clone()] as [Moment, Moment]
      },
      {
        label: 'Yesterday', 
        value: 'yesterday',
        range: [today.clone().subtract(1, 'day'), today.clone().subtract(1, 'day')] as [Moment, Moment]
      },
      {
        label: 'Last 7 Days',
        value: 'last7days',
        range: [today.clone().subtract(6, 'days'), today.clone()] as [Moment, Moment]
      },
      {
        label: 'Last 30 Days',
        value: 'last30days', 
        range: [today.clone().subtract(29, 'days'), today.clone()] as [Moment, Moment]
      },
      {
        label: 'This Month',
        value: 'thismonth',
        range: [today.clone().startOf('month'), today.clone().endOf('month')] as [Moment, Moment]
      },
      {
        label: 'Last Month',
        value: 'lastmonth',
        range: [
          today.clone().subtract(1, 'month').startOf('month'),
          today.clone().subtract(1, 'month').endOf('month')
        ] as [Moment, Moment]
      }
    ];
  }, []);

  const isDisabledDate = (currentDate: Moment): boolean => {
    const now = nowInSingapore();
    const maxPastDays = 93; // 31 * 3 days
    return currentDate.isAfter(now) || currentDate.isBefore(now.subtract(maxPastDays, 'days'));
  };

  const handlePresetSelect = (preset: typeof datePresets[0]) => {
    const validation = validateDateRange(preset.range[0], preset.range[1]);
    if (!validation.isValid) {
      message.error(validation.message);
      setDateValidationError(validation.message || null);
      return;
    }
    
    setDates(preset.range);
    setSelectedPreset(preset.value);
    setDateValidationError(null);
  };

  const getSelectedRangeText = () => {
    if (!dates || !dates[0] || !dates[1]) return '';
    const start = dates[0].format('MMM DD, YYYY');
    const end = dates[1].format('MMM DD, YYYY');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const handleSearch = async () => {
    const startDate = dates && dates[0] ? dates[0] : singaporeMoment();
    const endDate = dates && dates[1] ? dates[1] : singaporeMoment();

    startDate.set('hour', 0);
    startDate.set('minute', 0);
    startDate.set('second', 0);
    startDate.set('millisecond', 0);

    endDate.set('hour', 23);
    endDate.set('minute', 59);
    endDate.set('second', 59);
    endDate.set('millisecond', 999);

    // Validate date range before proceeding
    const validation = validateDateRange(startDate, endDate);
    if (!validation.isValid) {
      message.error(validation.message);
      setDateValidationError(validation.message || null);
      return;
    }

    if (endDate.diff(startDate, 'days') >= 1) {
      setIsDisbledChart(false);
    } else {
      setIsDisbledChart(true);
    }

    let queryPath = `&start=${startDate.valueOf()}`;
    queryPath += `&end=${endDate.valueOf()}`;
    queryPath += `&wabaId=${filtedWabaId}`;
    queryPath += finalPhoneNumber ? `&phoneNumbers=${finalPhoneNumber}` : '';

    fetchUsage(queryPath);
  };

  const handlePricingRefresh = async () => {
    if (!filtedWabaId) {
      message.warning('Please select a WABA first');
      return;
    }
    
    const startDate = dates && dates[0] ? dates[0] : singaporeMoment();
    const endDate = dates && dates[1] ? dates[1] : singaporeMoment();

    // Validate date range before proceeding
    const validation = validateDateRange(startDate, endDate);
    if (!validation.isValid) {
      message.error(validation.message);
      return;
    }

    let queryPath = `&start=${startDate.valueOf()}`;
    queryPath += `&end=${endDate.valueOf()}`;
    queryPath += `&wabaId=${filtedWabaId}`;
    queryPath += finalPhoneNumber ? `&phoneNumbers=${finalPhoneNumber}` : '';
    
    await fetchPricingOnly(queryPath);
    message.success('Pricing data refreshed successfully');
  };

  const handleCalendarChange = (val: RangeValue<moment.Moment>) => {
    if (val && val[0] && val[1]) {
      const validation = validateDateRange(val[0], val[1]);
      if (!validation.isValid) {
        message.error(validation.message);
        setDateValidationError(validation.message || null);
        return;
      }
      setDateValidationError(null);
    }
    
    setDates(val);
    setSelectedPreset(null); // Clear preset selection when manually selecting dates
  };

  const dateFormat = 'YYYY/MM/DD';
  const optionsWabaId = accounts.map((waba) => (
    <Option value={waba.wabaId} key={waba.wabaId}>
      {`${waba.wabaId}-${waba.name}`}
    </Option>
  ));
  const optionsPhoneNumbers = accounts
    .find((waba) => waba.wabaId == filtedWabaId)
    ?.phoneNumbers?.map((phoneNumber) => (
      <Option value={phoneNumber.value} key={phoneNumber.id}>
        {phoneNumber.value}
      </Option>
    ));
  
  /**
   * record structure:
   * 
   * date
   * phone number
   * conversation type
   * initiated by
   * number of conversation
   * charges
   */

  const createRecord = (
    date: string, 
    phone : string,
    conversationType : string,
    initBy : string,
    noConversation : number,
    charges : number
  ) : Collector => ({
    "Date" : date,
    "Phone Number" : phone,
    "Conversation Type" : conversationType,
    "Initiated By" : initBy,
    "Number of Conversation" : noConversation,
    "Charges" : `$${charges}`
  })

  const createRecords = (data : ConversationData & { date : string }, phoneNumbers :string) : Collector[] => {
    const collector : Collector[] = []
    if(data.FREE_ENTRY){
      collector.push(createRecord(
        data.date,
        phoneNumbers,
        "FREE_ENTRY_POINT",
        "",
        data.FREE_ENTRY,
        data.cost
      ))
    }
    if(data.FREE_TIER){
      collector.push(createRecord(
        data.date,
        phoneNumbers,
        "FREE_TIER",
        "",
        data.FREE_TIER,
        data.cost
      ))
    }
    if(data.PAID_BUSINESS){
      collector.push(createRecord(
        data.date,
        phoneNumbers,
        "REGULAR (paid)",
        "BUSINESS_INITIATED",
        data.PAID_BUSINESS,
        data.cost
      ))
    }
    if(data.PAID_USER){
      collector.push(createRecord(
        data.date,
        phoneNumbers,
        "REGULAR (paid)",
        "USER_INITIATED",
        data.PAID_USER,
        data.cost
      ))
    }
    return collector
  }

  const createPricingRecord = (): PricingCollector | null => {
    if (!pricingAnalytics || !record) return null;
    
    const dateRange = getSelectedRangeText();
    
    return {
      "Date Range": dateRange,
      "WABA ID": record.wabaId,
      "Total Messages Sent": pricingAnalytics.allMessages.messagesSent,
      "Total Messages Delivered": pricingAnalytics.allMessages.messagesDelivered,
      // "Total Messages Received": pricingAnalytics.allMessages.messagesReceived,
      "Marketing Messages": pricingAnalytics.allDeliveries.marketing,
      "Utility Messages": pricingAnalytics.allDeliveries.utility,
      "Authentication Messages": pricingAnalytics.allDeliveries.authentication,
      "Authentication International": pricingAnalytics.allDeliveries.authenticationInternational,
      "Service Messages": pricingAnalytics.allDeliveries.service,
      "Free Customer Service": pricingAnalytics.freeDeliveries.freeCustomerService,
      "Free Entry Point": pricingAnalytics.freeDeliveries.freeEntryPoint,
      "Total Cost": `$${pricingAnalytics.approximateCharges.total.toFixed(4)}`,
      "Marketing Cost": `$${pricingAnalytics.approximateCharges.marketing.toFixed(4)}`,
      "Utility Cost": `$${pricingAnalytics.approximateCharges.utility.toFixed(4)}`,
      "Authentication Cost": `$${pricingAnalytics.approximateCharges.authentication.toFixed(4)}`,
      "Authentication International Cost": `$${pricingAnalytics.approximateCharges.authenticationInternational.toFixed(4)}`,
      "Last Updated": pricingLastUpdated ? pricingLastUpdated.toLocaleString() : "N/A"
    };
  };

  const getExportData = async (exportType: 'all' | 'usage' | 'pricing' = 'all') => {
    try {
      const exportData: any = {};
      
      // Usage Analytics Sheet
      if ((exportType === 'all' || exportType === 'usage') && analyticsChart && record) {
        const phoneNumbers = _.map(record.phoneNumbers, (phoneNumber) => phoneNumber.value).join(' \r\n')
        const collectors = analyticsChart
        .map(data => createRecords(data, phoneNumbers))
        
        exportData['Usage Analytics'] = [].concat(...collectors as any[]);
      }
      
      // Pricing Analytics Sheet
      if ((exportType === 'all' || exportType === 'pricing')) {
        const pricingRecord = createPricingRecord();
        if (pricingRecord) {
          exportData['Pricing Analytics'] = [pricingRecord];
        }
      }
      
      // Return appropriate data structure based on what's available
      const availableSheets = Object.keys(exportData);
      if (availableSheets.length === 0) {
        return null;
      } else if (availableSheets.length === 1) {
        return exportData[availableSheets[0]];
      } else {
        return exportData;
      }
      
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  // Export functions for different types
  const handleExportAll = () => getExportData('all');
  const handleExportUsage = () => getExportData('usage');
  const handleExportPricing = () => getExportData('pricing');

  return (
    <Card className="text-center usage" title="Usage" style={{ maxHeight: '100vh', overflow: 'auto' }}>
      <div className="usage-controls" style={{ marginBottom: '1rem' }}>
        {/* Date Range Selection */}
        <div className="date-selection-section" style={{ marginBottom: '12px' }}>
          <div className="flex flex-ai-c" style={{ gap: '12px', marginBottom: '8px' }}>
            <CalendarOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
            <Text strong>Date Range:</Text>
            <Text type="secondary">{getSelectedRangeText()}</Text>
            <ClockCircleOutlined style={{ fontSize: '14px', color: '#8c8c8c', marginLeft: '8px' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>{getTimezoneDisplayText()}</Text>
          </div>
          
          {/* Quick Presets */}
          <div className="date-presets" style={{ marginBottom: '8px' }}>
            <Space wrap>
              {datePresets.map(preset => (
                <Button
                  key={preset.value}
                  size="small"
                  type={selectedPreset === preset.value ? 'primary' : 'default'}
                  onClick={() => handlePresetSelect(preset)}
                  disabled={isInputDisabled}
                >
                  {preset.label}
                </Button>
              ))}
            </Space>
          </div>
          
          {/* Custom Date Range Picker */}
          <div>
            <RangePicker
              allowEmpty={[false, false]}
              defaultValue={[singaporeMoment(), singaporeMoment()]}
              picker={'date'}
              format={dateFormat}
              value={dates}
              onChange={handleCalendarChange}
              disabledDate={isDisabledDate}
              placeholder={['Start Date', 'End Date']}
              style={{ width: '300px' }}
              disabled={isInputDisabled}
              status={dateValidationError ? 'error' : ''}
              aria-label="Select date range for analytics"
              aria-describedby={dateValidationError ? 'date-error' : undefined}
            />
            {dateValidationError && (
              <div 
                id="date-error"
                role="alert"
                aria-live="polite"
                style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}
              >
                {dateValidationError}
              </div>
            )}
          </div>
        </div>
        
        {/* Filters Section */}
        <div className="filters-section flex  flex-ai-c">
          <Select
            showSearch
            disabled={isInputDisabled}
            defaultValue="Choose Waba"
            autoFocus
            style={{ width: 300 }}
            placeholder="Filter with WabaId"
            dropdownStyle={{ maxWidth: '400px' }}
            onSelect={(value) => {
              setFiltedWabaId(value);
              setFiltedPhoneNumber('all');
            }}
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {optionsWabaId}
          </Select>
          {filtedWabaId != 'all' && (
            <Select
              showSearch
              disabled={isInputDisabled || filtedWabaId == 'all'}
              value={filtedWabaId == 'all' ? 'Filter All Phone Numbers' : filtedPhoneNumber}
              defaultValue="Filter All Phone Numbers"
              style={{ width: 180, marginLeft: '20px' }}
              placeholder="Filter with Phone Numbers"
              onSelect={(value) => setFiltedPhoneNumber(value)}
            >
              <Option value="all">Filter all Phone Numbers</Option>
              {optionsPhoneNumbers}
            </Select>
          )}
          <Tooltip placement="top" title={filtedWabaId ? '' : 'Choose waba to analysis'}>
            <Button
              style={{ marginLeft: '20px' }}
              type="primary"
              icon={<StockOutlined />}
              disabled={isInputDisabled || !filtedWabaId}
              onClick={handleSearch}
              loading={isInputDisabled}
            >
              Analyse
            </Button>
          </Tooltip>
          <ExportDropdown
            style={{ marginLeft: '5px' }}
            onExportAll={handleExportAll}
            onExportUsage={handleExportUsage}
            onExportPricing={handleExportPricing}
            disabled={record === null}
            hasUsageData={!!(analyticsChart && record)}
            hasPricingData={!!pricingAnalytics}
          />
        </div>
      </div>
      {record && (
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          style={{ marginTop: '16px' }}
        >
          <TabPane tab="Usage Analytics" key="usage">
            {/* Error Display for Usage */}
            {usageError && (
              <div style={{ 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '6px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: '#a8071a'
              }}>
                <strong>Usage Data Error:</strong> {usageError}
                <Button 
                  type="link" 
                  size="small" 
                  onClick={handleSearch}
                  style={{ marginLeft: '8px', padding: 0 }}
                >
                  Retry
                </Button>
              </div>
            )}
            
            <div className="usage-analytics-controls">
              <Select
                style={{ width: 120 }}
                value={analysisView}
                onChange={setAnalysisView}
                disabled={isInputDisabled}
              >
                <Option value="table">Table View</Option>
                <Option value="chart" disabled={isDisbledChart}>Chart View</Option>
              </Select>
            </div>
            {analysisView === 'table' && (
              <UsageTable loading={isInputDisabled} usageList={[record]} />
            )}
            {analysisView === 'chart' && !isDisbledChart && (
              <UsageChart
                loading={isInputDisabled}
                usageList={analyticsChart}
                totalCost={record.totalCost}
                totalConversation={
                  record.FREE_ENTRY + 
                  record.FREE_TIER + 
                  record.PAID_BUSINESS +
                  record.PAID_USER
                }
              />
            )}
          </TabPane>
          <TabPane tab="Pricing Analytics" key="pricing">
            <div className="pricing-analytics-section">
              {/* Error Display */}
              {pricingError && (
                <div style={{ 
                  backgroundColor: '#fff2f0', 
                  border: '1px solid #ffccc7', 
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#a8071a'
                }}>
                  <strong>Pricing Data Error:</strong> {pricingError}
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={handlePricingRefresh}
                    style={{ marginLeft: '8px', padding: 0 }}
                  >
                    Retry
                  </Button>
                </div>
              )}
              
              <UsagePricingCards 
                usage={record} 
                pricingAnalytics={pricingAnalytics}
                loading={isInputDisabled || isPricingLoading} 
              />
            </div>
          </TabPane>
        </Tabs>
      )}
    </Card>
  );
}

export default UsagePage;
